import KY from 'ky'
// import KY  from 'ky-universal'
import type { KyInstance } from 'ky/distribution/types/ky'
import type { Options, ResponsePromise } from 'ky';
import growl from '../../ui/growl'

export { KyInstance, Options, ResponsePromise }

// Global loader integration
let globalLoaderEnabled = true;

// subscriber's handlers, allows to add subs without needing to create a new ky
const subs = {
  before: [],
  after: []
}

// Global loader hooks
const globalLoaderHooks = {
  beforeRequest: [
    (request: Request) => {
      // Call all before subscribers
      subs.before.forEach(sub => sub(request))

      // Show global loader if available and enabled
      if (globalLoaderEnabled && typeof window !== 'undefined') {
        try {
          window.showGlobalLoader?.({
            text: 'Loading...',
            progress: 0,
            overlay: false // Don't block UI, just show progress bar
          });
          let progress = 0;
          const interval = setInterval(() => {
            progress += 10;
            if (progress <= 90) {
              window.setGlobalLoaderProgress(progress);
            } else {
              clearInterval(interval);
              setTimeout(() => {
                window.hideGlobalLoader();
              }, 500);
            }
          }, 200);

        } catch (e) {
          // Ignore errors if global loader is not available
        }
      }
    }
  ],
  afterResponse: [
    (_request: Request, _options: any, response: Response) => {
      // Call all after subscribers
      subs.after.forEach(sub => sub(_request, _options, response))

      if (response.status === 403) {
        //get the path from the url so user knows what they tried to access
        const path =response.url.split('/api')[1].split('?')[0]
        growl.error(path, 'Access Forbidden', { duration: 5000 })
      }

      // Hide global loader if available and enabled
      if (globalLoaderEnabled && typeof window !== 'undefined') {
        try {
          // Complete the progress bar
          window.setGlobalLoaderProgress?.(100);

          // Small delay to show completion, then hide
          setTimeout(() => {
            window.hideGlobalLoader?.();
          }, 200);
        } catch (e) {
          // Ignore errors if global loader is not available
        }
      }
    }
  ]
}

const defaults = {
  prefixUrl: '/api',
  timeout: 60000, //we have some long running processes so set to 1 minute
  credentials: 'include',
  hooks: globalLoaderHooks,
  //TODO look into this further, for now we turn off any caching
  // might be better way? https://stackoverflow.com/a/42518434
  headers: {
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache'
  }
}

let kyInstance: KyInstance //kyInstance holder

function getKy(): KyInstance { return kyInstance }

function makeKy({ apiConfig }) {

  if (apiConfig.enableAuthHeader) {
    KyFactory.enableAuthHeader()
  }

  // Enable global loader by default
  KyFactory.enableGlobalLoader(true)

  KyFactory.defaults.prefixUrl = apiConfig.apiUrl
  return KyFactory.build()
}

const KyFactory = {
  defaults,

  /**
   * the current ky instance
   * @returns {KyInstance} the instance
   */
  getKy,

  /**
   * builds the default ky with any extra overide options passed in
   * shouldn't need to do this more than once really, and then KyFactory.getKy can be used
   *
   * @param {Object} opts extra options
   * @returns {KyInstance} the instance
   */
  build(opts?: any): KyInstance {
    kyInstance = KY.create({ ...this.defaults, ...opts })
    return kyInstance
  },

  /**
   * can subscribe to ky hooks and not need to recreate a ky to have them fired
   * Used for loading indicators and logging
   *
   * @param {string} type before or after
   * @param {function} handler funtion, see ky for what it can expect depending on the before or after
   * @returns {function} a function to call to unsub to avoid mem leaks
   */
  subscribe(type, handler) {
    subs[type] = [...subs[type], handler] // add handler to the array of subscribers
    return () => subs[type] = subs[type].filter(sub => sub !== handler)   // return unsubscribe function
  },

  /**
   * opinionated way to add Bearer token header. add a beforeRequest sub to add the header
   */
  enableAuthHeader() {
    const authHandler = request => {
      if (request.url.indexOf('oauth/token') > -1) return
      request.headers.set('Authorization', 'Bearer ' + localStorage.getItem('bearer'))
    }
    this.subscribe('before', authHandler)
  },

  /**
   * Enable or disable global loader integration
   * @param {boolean} enabled - Whether to enable global loader
   */
  enableGlobalLoader(enabled: boolean = true) {
    globalLoaderEnabled = enabled;
  }
}

export { KyFactory, getKy, makeKy }

